import { supabase } from "@/integrations/supabase/client";
import { calculateMonthlyPrice } from "./pricingCalculator";

export interface SubscriptionStatus {
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'payment_failed' | 'pending_payment' | 'exempt' | null;
  currentUserCount: number;
  trialEndDate: string | null;
  currentPeriodEnd: string | null;
  paymentFailedAt: string | null;
  daysRemaining: number | null;
  /** Price that will be charged at the next renewal based on the current user count. */
  monthlyPrice: number;
  /** Price currently configured on the live Stripe subscription (what's billed at end of THIS cycle). May differ from monthlyPrice if users have changed since the last sync. Null when no live subscription. */
  currentBilledPrice: number | null;
  hasPaymentMethod?: boolean;
  paymentMethodType?: string | null;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
  isExempt?: boolean;
  exemptionReason?: string | null;
}

/**
 * Get subscription status for the current user's company
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Get user profile to find company and check exemption
  const { data: profile } = await supabase
    .from('profiles')
    .select('company_id, subscription_exempt')
    .eq('id', user.id)
    .single();

  // If user is exempt, return exempt status immediately
  if (profile?.subscription_exempt) {
    return {
      status: 'exempt',
      currentUserCount: 0,
      trialEndDate: null,
      currentPeriodEnd: null,
      paymentFailedAt: null,
      daysRemaining: null,
      monthlyPrice: 0,
      currentBilledPrice: null,
      isExempt: true,
      exemptionReason: 'User has been granted subscription exemption'
    };
  }

  if (!profile?.company_id) return null;

  // Check if company is exempt
  const { data: company } = await supabase
    .from('companies')
    .select('subscription_exempt, exemption_reason')
    .eq('id', profile.company_id)
    .single();

  if (company?.subscription_exempt) {
    return {
      status: 'exempt',
      currentUserCount: 0,
      trialEndDate: null,
      currentPeriodEnd: null,
      paymentFailedAt: null,
      daysRemaining: null,
      monthlyPrice: 0,
      currentBilledPrice: null,
      isExempt: true,
      exemptionReason: company.exemption_reason || 'Company has been granted subscription exemption'
    };
  }

  // Get company subscription
  const { data: subscription } = await supabase
    .from('company_subscriptions')
    .select('*')
    .eq('company_id', profile.company_id)
    .single();

  // Get payment method details and live user count from edge function
  let hasPaymentMethod = false;
  let paymentMethodType: string | null = null;
  let paymentMethodBrand: string | null = null;
  let paymentMethodLast4: string | null = null;
  let isExempt = false;
  let exemptionReason: string | null = null;
  let userCount = 0;
  let currentPeriodEnd: string | null = null;
  let currentBilledPrice: number | null = null;

  try {
    const { data: edgeFunctionData } = await supabase.functions.invoke('check-subscription');
    
    if (edgeFunctionData && !edgeFunctionData.error) {
      if (edgeFunctionData.is_exempt) {
        isExempt = true;
        exemptionReason = edgeFunctionData.exemption_reason || null;
      }
      hasPaymentMethod = edgeFunctionData.has_payment_method || false;
      paymentMethodType = edgeFunctionData.payment_method_type || null;
      paymentMethodBrand = edgeFunctionData.payment_method_brand || null;
      paymentMethodLast4 = edgeFunctionData.payment_method_last4 || null;
      userCount = edgeFunctionData.live_user_count || 0;
      currentPeriodEnd = edgeFunctionData.current_period_end ?? null;
      // Stripe stores unit_amount in pence; convert to pounds for the UI.
      currentBilledPrice = typeof edgeFunctionData.current_unit_amount === 'number'
        ? edgeFunctionData.current_unit_amount / 100
        : null;
    }
  } catch (error) {
    console.error('Error fetching subscription details:', error);
  }

  if (!subscription) {
    return {
      status: null,
      currentUserCount: userCount,
      trialEndDate: null,
      currentPeriodEnd: currentPeriodEnd,
      paymentFailedAt: null,
      daysRemaining: null,
      monthlyPrice: calculateMonthlyPrice(userCount),
      currentBilledPrice,
      hasPaymentMethod,
      paymentMethodType,
      paymentMethodBrand,
      paymentMethodLast4,
      isExempt,
      exemptionReason
    };
  }

  let daysRemaining = null;
  if (subscription.status === 'trialing' && subscription.trial_end_date) {
    const endDate = new Date(subscription.trial_end_date);
    const now = new Date();
    daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  return {
    status: subscription.status as SubscriptionStatus['status'],
    currentUserCount: userCount,
    trialEndDate: subscription.trial_end_date,
    currentPeriodEnd: currentPeriodEnd ?? subscription.current_period_end,
    paymentFailedAt: subscription.payment_failed_at,
    daysRemaining,
    monthlyPrice: calculateMonthlyPrice(userCount),
    currentBilledPrice,
    hasPaymentMethod,
    paymentMethodType,
    paymentMethodBrand,
    paymentMethodLast4,
    isExempt,
    exemptionReason
  };
}

/**
 * Update subscription user count
 */
export async function updateSubscriptionUserCount(
  companyId: string,
  newUserCount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('company_subscriptions')
      .update({ current_user_count: newUserCount })
      .eq('company_id', companyId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error updating user count:', error);
    return {
      success: false,
      error: error.message || 'Failed to update user count'
    };
  }
}

/**
 * Mark subscription payment as failed
 */
export async function markPaymentFailed(
  companyId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('company_subscriptions')
      .update({
        status: 'payment_failed',
        payment_failed_at: new Date().toISOString()
      })
      .eq('company_id', companyId);

    if (error) throw error;

    // Update company status
    await supabase
      .from('companies')
      .update({ subscription_status: 'payment_failed' })
      .eq('id', companyId);

    return { success: true };
  } catch (error: any) {
    console.error('Error marking payment as failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark payment as failed'
    };
  }
}

/**
 * Check if payment grace period has expired (3 days)
 */
export function hasGracePeriodExpired(paymentFailedAt: string | null): boolean {
  if (!paymentFailedAt) return false;
  
  const failedDate = new Date(paymentFailedAt);
  const now = new Date();
  const daysSinceFailed = Math.floor((now.getTime() - failedDate.getTime()) / (1000 * 60 * 60 * 24));
  
  return daysSinceFailed > 3;
}

/**
 * Check if user should be blocked from accessing the system
 */
export async function shouldBlockAccess(): Promise<boolean> {
  // First check if user is exempt from subscription requirements
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  
  const { data: profile } = await supabase
    .from('profiles')
    .select('subscription_exempt, company_id')
    .eq('id', user.id)
    .single();
  
  // If user is exempt, never block access
  if (profile?.subscription_exempt) {
    return false;
  }
  
  // Check if their company is exempt
  if (profile?.company_id) {
    const { data: company } = await supabase
      .from('companies')
      .select('subscription_exempt')
      .eq('id', profile.company_id)
      .single();
    
    if (company?.subscription_exempt) {
      return false;
    }
  }
  
  const status = await getSubscriptionStatus();
  
  // If no subscription status at all (shouldn't happen with our trigger), block access
  if (!status || !status.status) {
    return true;
  }
  
  // Block if payment failed and grace period expired
  if (status.status === 'payment_failed' && status.paymentFailedAt) {
    return hasGracePeriodExpired(status.paymentFailedAt);
  }
  
  // Block if trial expired (days remaining is 0 or negative)
  if (status.status === 'trialing' && status.daysRemaining !== null && status.daysRemaining <= 0) {
    return true;
  }
  
  // Block if subscription is canceled or unpaid
  if (['canceled', 'unpaid', 'incomplete_expired'].includes(status.status)) {
    return true;
  }
  
  return false;
}
